// useDeskReadiness — the export readiness (Studio desk decision 3) of each
// module or course open on the desk, and of what Export sends out (the
// Needs you tab's Before export). Shell-side: it asks the
// server whenever what is open or the library changes (a document planned,
// written or taken out moves the line), and hands the answers to the desk.
// A failed ask leaves that container without a line.

export { useDeskReadiness };

import { Result } from "@bilda/utils";
import { useEffect, useState } from "react";
import {
    type DeskState,
    deskBinders,
} from "../../../components/Studio/studioDesk.js";
import type { ExportReadiness } from "../../ports/brief.js";
import type { AppPorts, Library } from "../../ports.js";

const useDeskReadiness = (
    ports: AppPorts,
    desk: DeskState,
    library: Library | null,
    // Also this one (what Export sends out), open or not.
    also: string | undefined = undefined,
): ReadonlyMap<string, ExportReadiness> => {
    const [readiness, setReadiness] = useState<
        ReadonlyMap<string, ExportReadiness>
    >(new Map());
    const open = [
        ...new Set([
            ...deskBinders(desk).map((e) => e.id),
            ...(also === undefined ? [] : [also]),
        ]),
    ].join(" ");
    useEffect(() => {
        if (library === null) return;
        const ids = open === "" ? [] : open.split(" ");
        let current = true;
        const go = async () => {
            const answers = await Promise.all(
                ids.map(async (id) => ({
                    id,
                    r: await ports.loadExportReadiness(id),
                })),
            );
            if (!current) return;
            setReadiness(
                new Map(
                    answers.flatMap(
                        ({ id, r }): readonly [string, ExportReadiness][] =>
                            Result.isOk(r) ? [[id, r.value]] : [],
                    ),
                ),
            );
        };
        void go();
        return () => {
            current = false;
        };
    }, [ports, open, library]);
    return readiness;
};
